import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Bell, CalendarDays, Eye, Heart, Sparkles, X } from 'lucide-react'
import { useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, useNavigate } from 'react-router-dom'
import { Popover, PopoverContent, PopoverTrigger, cn } from '@alerte-immo/ui'
import { relTime } from '@/lib/formatters'
import { queryKeys } from '@/lib/queryKeys'
import { SheetGrabber, useBottomSheetDrag } from '@/lib/useBottomSheetDrag'
import { useIsDesktop } from '@/lib/useIsDesktop'
import {
  deleteNotification,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '../api'
import type { AppNotification, NotificationKind } from '../types'

/** Where a notification click lands, by kind. */
export function notificationTarget(n: AppNotification): string {
  // unseen_reminder carries no listings of its own: apply payload.filter
  // ({ seen: false }) on the list so the filtered count matches the title.
  if (n.kind === 'unseen_reminder') return '/app/biens?seen=false'
  return `/app/notifications/${n.id}`
}

/** Délai pendant lequel une suppression reste annulable avant l'appel API. */
export const UNDO_DELETE_MS = 5000

/**
 * Sous md, le popover Radix (ancré, étroit) devient un bottom sheet plein
 * écran — même pattern que les filtres de /app/biens. Le wrapper Radix porte
 * un transform, donc le sheet ne peut pas vivre dans PopoverContent : on
 * bascule de composant selon le viewport.
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false)
  const isDesktop = useIsDesktop()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data, isError } = useQuery({
    queryKey: queryKeys.notifications,
    queryFn: () => listNotifications(20),
    // Keep the badge fresh during long sessions without a page reload.
    refetchInterval: 60_000,
  })
  const notifications = data?.items ?? []
  const unread = data?.meta.unreadCount ?? 0

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.notifications })

  const markAll = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: invalidate,
  })
  const markRead = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: invalidate,
  })
  const remove = useMutation({
    mutationFn: deleteNotification,
    onSuccess: invalidate,
  })

  // Suppression différée : la ligne affiche « Annuler » pendant UNDO_DELETE_MS,
  // puis l'appel part. Les timers vivent ici (pas dans le panneau) : fermer le
  // panneau ne perd pas la suppression en attente.
  const deleteTimers = useRef(new Map<number, number>())
  const [pendingDeletes, setPendingDeletes] = useState<number[]>([])
  // DELETE parti : la ligne reste masquée, sans « Annuler » (plus annulable).
  const [sentDeletes, setSentDeletes] = useState<number[]>([])
  const dropPending = (id: number) => setPendingDeletes((ids) => ids.filter((x) => x !== id))
  const scheduleDelete = (id: number) => {
    setPendingDeletes((ids) => [...ids, id])
    deleteTimers.current.set(
      id,
      window.setTimeout(() => {
        deleteTimers.current.delete(id)
        dropPending(id)
        setSentDeletes((ids) => [...ids, id])
        // Masquée jusqu'au refetch ; réapparaît si l'appel échoue.
        void remove
          .mutateAsync(id)
          .catch(() => undefined)
          .finally(() => setSentDeletes((ids) => ids.filter((x) => x !== id)))
      }, UNDO_DELETE_MS),
    )
  }
  const undoDelete = (id: number) => {
    window.clearTimeout(deleteTimers.current.get(id))
    deleteTimers.current.delete(id)
    dropPending(id)
  }

  const close = () => setOpen(false)
  // Bottom sheet mobile redimensionnable + glisser-pour-fermer (hook partagé
  // avec les panneaux fiche / filtres).
  const sheetRef = useRef<HTMLDivElement>(null)
  const drag = useBottomSheetDrag(!isDesktop, sheetRef, close)

  const openNotification = (n: AppNotification) => {
    if (!n.read) markRead.mutate(n.id)
    close()
    navigate(notificationTarget(n))
  }

  const trigger = (
    <button
      type="button"
      aria-label={
        unread > 0 ? `Notifications, ${unread} non lues` : 'Notifications'
      }
      onClick={isDesktop ? undefined : () => setOpen((o) => !o)}
      className={cn(
        'relative inline-flex h-9 w-9 items-center justify-center rounded-md border transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        open
          ? 'border-primary/40 bg-surface text-foreground'
          : 'border-border bg-surface text-muted hover:text-foreground hover:bg-surface-elevated',
      )}
    >
      <Bell className="h-[18px] w-[18px]" />
      {unread > 0 ? (
        <span className="absolute -right-1.5 -top-1.5 grid h-[18px] min-w-[18px] place-items-center rounded-full border-2 border-background bg-primary px-1 text-[11px] font-semibold leading-none tabular-nums text-primary-foreground">
          {unread}
        </span>
      ) : null}
    </button>
  )

  const panelBody = (
    <>
      <header className="flex items-center gap-2 border-b border-border px-4 py-3">
        <span className="font-semibold tracking-tight">Notifications</span>
        {unread > 0 ? (
          <span className="rounded-full bg-primary-soft px-2 py-px font-mono text-[11px] font-semibold text-primary">
            {unread} non lues
          </span>
        ) : null}
        {unread > 0 ? (
          <button
            type="button"
            onClick={() => markAll.mutate()}
            disabled={markAll.isPending}
            className="ml-auto inline-flex items-center gap-1.5 text-xs text-muted transition-colors hover:text-foreground disabled:opacity-50"
          >
            Tout marquer lu
          </button>
        ) : null}
      </header>

      {isError ? (
        <p className="px-4 py-10 text-center text-sm text-muted max-md:flex max-md:flex-1 max-md:items-center max-md:justify-center">
          Impossible de charger les notifications.
        </p>
      ) : notifications.length === 0 ? (
        <div className="px-4 py-12 text-center max-md:flex max-md:flex-1 max-md:flex-col max-md:items-center max-md:justify-center">
          <Bell className="mx-auto mb-3 h-6 w-6 text-muted" />
          <p className="text-sm font-medium">Tu es à jour</p>
          <p className="mt-1 text-[13px] text-muted">
            Pas de nouvelle notification.
          </p>
        </div>
      ) : (
        <div className="max-h-[430px] overflow-auto p-2.5 max-md:max-h-none max-md:min-h-0 max-md:flex-1">
          {notifications.map((n) =>
            sentDeletes.includes(n.id) ? null : pendingDeletes.includes(n.id) ? (
              <div
                key={n.id}
                role="status"
                className="flex items-center justify-between gap-3 rounded-lg px-2 py-2.5 text-[13px] text-muted"
              >
                Notification supprimée.
                <button
                  type="button"
                  autoFocus
                  onClick={() => undoDelete(n.id)}
                  className="min-h-7 rounded px-2 font-semibold text-foreground transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  Annuler
                </button>
              </div>
            ) : (
              <NotificationItem
                key={n.id}
                n={n}
                onOpen={() => openNotification(n)}
                onDelete={() => scheduleDelete(n.id)}
              />
            ),
          )}
        </div>
      )}

      <footer className="flex items-center justify-between border-t border-border bg-background-2 px-4 py-2.5">
        <Link
          to="/app/account#email-volume"
          onClick={close}
          className="text-[13px] font-semibold text-foreground transition-colors hover:text-primary"
        >
          Préférences de notification
        </Link>
      </footer>
    </>
  )

  if (!isDesktop) {
    // Le sheet doit échapper au contexte d'empilement du header (sticky z-30) :
    // sinon scrim/sheet restent plaqués au niveau z-30 et le bouton cookie
    // (z-30) comme le webchat Botpress (injecté dans body, z-index ~2.1e9)
    // passent par-dessus. On porte donc dans body et on monte au-dessus de
    // tout (max int 32 bits) pour que le sheet opaque, plein écran en bas,
    // recouvre ces deux boutons flottants.
    return (
      <>
        {trigger}
        {open
          ? createPortal(
              <>
                <div
                  className="fixed inset-0 z-[2147483647] bg-foreground/20"
                  onClick={close}
                  aria-hidden="true"
                />
                <div
                  ref={sheetRef}
                  role="dialog"
                  aria-label="Notifications"
                  style={drag.sheetStyle}
                  className={cn(
                    'fixed inset-x-0 bottom-0 z-[2147483647] flex flex-col rounded-t-xl border-t border-border bg-surface pb-[max(env(safe-area-inset-bottom),0.5rem)] shadow-2xl',
                    // Avant tout redimensionnement : taille confortable par défaut.
                    // Une fois piloté par le drag, la hauteur inline prend le relais.
                    !drag.sheetStyle && 'max-h-[80vh] min-h-[50vh]',
                  )}
                >
                  <SheetGrabber drag={drag} label="Redimensionner les notifications" className="" />
                  {panelBody}
                </div>
              </>,
              document.body,
            )
          : null}
      </>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className="w-[min(392px,calc(100vw-1rem))] p-0" align="end" sideOffset={10}>
        {panelBody}
      </PopoverContent>
    </Popover>
  )
}

// ---------------------------------------------------------------------------

const glyphStyles: Record<NotificationKind, string> = {
  new_listings: 'bg-primary-soft text-primary',
  recommendations: 'bg-destructive/10 text-destructive',
  unseen_reminder: 'bg-warning/15 text-warning',
  weekly_recap: 'bg-success/15 text-success',
}

const glyphIcons: Record<NotificationKind, typeof Sparkles> = {
  new_listings: Sparkles,
  recommendations: Heart,
  unseen_reminder: Eye,
  weekly_recap: CalendarDays,
}

function Glyph({ kind }: { kind: NotificationKind }) {
  // Le back peut dériver du contrat et livrer un `kind` inconnu : sans repli,
  // `glyphIcons[kind]` vaut `undefined` et `<Icon />` casse tout le panneau
  // (React #130). On retombe sur la cloche neutre plutôt que de planter.
  const Icon = glyphIcons[kind] ?? Bell
  return (
    <span
      className={cn(
        'grid h-[30px] w-[30px] flex-none place-items-center rounded-lg',
        glyphStyles[kind] ?? 'bg-surface text-muted',
      )}
    >
      <Icon className="h-4 w-4" />
    </span>
  )
}

function NotificationItem({
  n,
  onOpen,
  onDelete,
}: {
  n: AppNotification
  onOpen: () => void
  onDelete: () => void
}) {
  return (
    <div className="group relative flex items-start gap-2.5 rounded-lg px-2 py-2.5 transition-colors hover:bg-surface">
      <button
        type="button"
        onClick={onOpen}
        className="absolute inset-0 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label={n.title}
      />
      {!n.read ? (
        <span className="mt-1.5 h-1.5 w-1.5 flex-none rounded-full bg-primary" />
      ) : (
        <span className="mt-1.5 h-1.5 w-1.5 flex-none" />
      )}
      <Glyph kind={n.kind} />
      <div className={cn('min-w-0 flex-1', n.read && 'opacity-60')}>
        {/* `title` arrive localisé et pluralisé du back — affiché tel quel. */}
        <p className="text-[13.5px] leading-snug">{n.title}</p>
        {n.body ? <p className="mt-0.5 text-[12.5px] text-muted">{n.body}</p> : null}
      </div>
      {/* L'horodatage et la suppression partagent le coin haut-droit à la
          souris : la date au repos, la croix au survol ou au focus clavier.
          Évite une colonne dédiée à la date qui rognait la largeur du titre.
          Écran tactile (pas de survol) : la croix est toujours visible, sous la
          date, sans la chevaucher. */}
      <div className="relative flex flex-none flex-col items-end gap-1 self-start pt-0.5">
        <span className="block text-right font-mono text-[11px] leading-none text-muted transition-opacity group-hover:opacity-0 group-has-[[data-delete]:focus-visible]:opacity-0">
          {relTime(n.createdAt)}
        </span>
        <button
          type="button"
          data-delete
          onClick={onDelete}
          aria-label="Supprimer la notification"
          className={cn(
            'absolute -top-1.5 right-0 z-10 grid h-6 w-6 place-items-center rounded bg-surface text-muted opacity-0 transition-opacity hover:text-foreground group-hover:opacity-100',
            'focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            // Reste positionné (relative) pour passer au-dessus du bouton-ligne absolu.
            '[@media(hover:none)]:relative [@media(hover:none)]:top-0 [@media(hover:none)]:-mr-1 [@media(hover:none)]:h-7 [@media(hover:none)]:w-7 [@media(hover:none)]:opacity-100',
          )}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  )
}
